import type { RecommendationContextAxis } from "@memorang/types";
import type {
	AxisFilters,
	EffectiveMastery,
	FullCell,
} from "../contract/recommendation-context-types";
import { trackKey } from "./cell-model";
import { observedAsEffective } from "./mastery-inference";

const timestamp = (value: string | null): number => {
	const parsed = value ? new Date(value).getTime() : Number.NEGATIVE_INFINITY;
	return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
};

const preferenceRank = (
	cell: FullCell,
	preferences: AxisFilters | undefined,
	axes: RecommendationContextAxis[],
): number =>
	axes.reduce((total, axis, axisIndex) => {
		const values = preferences?.[axis.id] ?? [];
		const index = values.indexOf(cell.coordinates[axis.id]);
		return (
			total + (index < 0 ? values.length : index) * (axes.length - axisIndex)
		);
	}, 0);

export const rankCandidates = ({
	candidates,
	effective,
	minimumEvidence,
	preferences,
	axes,
	stableOrder,
}: {
	candidates: FullCell[];
	effective: ReadonlyMap<string, EffectiveMastery>;
	minimumEvidence: number;
	preferences?: AxisFilters;
	axes: RecommendationContextAxis[];
	stableOrder: (left: FullCell, right: FullCell) => number;
}): FullCell[] => {
	const trackAxes = axes.filter(({ role }) => role === "TRACK");
	const lastAnsweredByTrack = new Map<string, number>();
	for (const cell of candidates) {
		const key = trackKey(cell, trackAxes);
		lastAnsweredByTrack.set(
			key,
			Math.max(
				lastAnsweredByTrack.get(key) ?? Number.NEGATIVE_INFINITY,
				timestamp(cell.mastery.lastAnsweredAt),
			),
		);
	}
	return [...candidates].sort((left, right) => {
		const leftEffective =
			effective.get(left.source.key) ?? observedAsEffective(left);
		const rightEffective =
			effective.get(right.source.key) ?? observedAsEffective(right);
		return (
			Number(left.mastery.distinctItemCount >= minimumEvidence) -
				Number(right.mastery.distinctItemCount >= minimumEvidence) ||
			left.mastery.distinctItemCount - right.mastery.distinctItemCount ||
			(leftEffective.value ?? 0) - (rightEffective.value ?? 0) ||
			preferenceRank(left, preferences, axes) -
				preferenceRank(right, preferences, axes) ||
			(lastAnsweredByTrack.get(trackKey(left, trackAxes)) ??
				Number.NEGATIVE_INFINITY) -
				(lastAnsweredByTrack.get(trackKey(right, trackAxes)) ??
					Number.NEGATIVE_INFINITY) ||
			stableOrder(left, right)
		);
	});
};
